import { useEffect, useState } from "react";
import api from "../lib/api";
import { runAiJob } from "../lib/aiJob";
import { LOSS_CATEGORIES } from "../lib/lossCategories";
import { toast } from "sonner";
import { FileText, Sparkles, Send, RefreshCw, Download, ChevronRight, Trash2 } from "lucide-react";

const STATUS = {
  draft: ["DRAFT", "#E2711D"], revised: ["REVISED", "#E2711D"],
  pending_approval: ["PENDING APPROVAL", "#3E8F99"], approved: ["APPROVED", "#2F7A3E"],
  sent: ["SENT", "#2F7A3E"],
};

function Pill({ status }) {
  const [l, c] = STATUS[status] || [status, "#94A3AD"];
  return <span className="font-mono text-[9px] uppercase border px-1 py-0.5" style={{ color: c, borderColor: c }}>{l}</span>;
}

// Lightweight markdown → JSX (headings / bullets / bold / table rows).
function renderMd(md) {
  const out = [];
  (md || "").split("\n").forEach((raw, i) => {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { out.push(<div key={i} className="h-2" />); return; }
    const bold = (t) => t.split(/(\*\*.+?\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>);
    if (line.startsWith("### ")) out.push(<h3 key={i} className="text-sm font-semibold text-[#1C2530] mt-3">{line.slice(4)}</h3>);
    else if (line.startsWith("## ")) out.push(<h2 key={i} className="text-base font-semibold text-[#1C2530] mt-4 border-b border-[#DFDAD0] pb-1">{line.slice(3)}</h2>);
    else if (line.startsWith("# ")) out.push(<h1 key={i} className="text-lg font-bold text-[#1C2530] mt-2">{line.slice(2)}</h1>);
    else if (/^[-*] /.test(line.trim())) out.push(<div key={i} className="flex gap-2 text-sm text-[#1C2530]/85 ml-2"><span>•</span><span>{bold(line.trim().slice(2))}</span></div>);
    else if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line.slice(1, -1).split("|").map((c) => c.trim());
      if (cells.join("").replace(/[-: ]/g, "") === "") return;
      out.push(<div key={i} className="flex gap-3 text-xs font-mono text-[#1C2530]/80 border-b border-[#DFDAD0] py-1">{cells.map((c, j) => <span key={j} className="flex-1">{c}</span>)}</div>);
    } else out.push(<p key={i} className="text-sm text-[#1C2530]/85 leading-relaxed">{bold(line)}</p>);
  });
  return out;
}

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

function MarginBadge({ p }) {
  if (p.margin_amount == null) return null;
  const pct = p.margin_pct ?? 0;
  const tone = pct >= 40 ? "border-[#2F7A3E] text-[#2F7A3E]" : pct >= 20 ? "border-[#C99A1E] text-[#8a6a12]" : "border-[#B23A2E] text-[#B23A2E]";
  return (
    <span data-testid="proposal-margin" title={`Net ${p.currency} ${fmt(p.net_amount)} − partner cost ${fmt(p.partner_cost)} = gross margin ${fmt(p.margin_amount)}`}
      className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[9px] uppercase ${tone}`}>
      Margin {fmt(p.margin_amount)} {p.currency} · {pct}%{p.partner_cost ? ` · partners ${fmt(p.partner_cost)}` : ""}
    </span>
  );
}

export default function ProposalPanel({ leadId, region, onChange }) {
  const [offerings, setOfferings] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ offering_id: "", title: "", list_amount: "", discount_amount: "", instructions: "", currency: "" });
  const [curCfg, setCurCfg] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [sel, setSel] = useState(null); // {proposal, versions}
  const [verIdx, setVerIdx] = useState(0);
  const [revise, setRevise] = useState({ open: false, instructions: "", change_note: "", list_amount: "", discount_amount: "" });
  const [setup, setSetup] = useState(null);
  const [envelope, setEnvelope] = useState("combined");
  const [compose, setCompose] = useState({ open: false, to: "", subject: "", body: "", mode: "combined", sending: false });

  const loadOfferings = () => api.get("/proposals/offerings").then((r) => setOfferings(r.data.filter((o) => o.status !== "archived"))).catch(() => {});
  const loadList = () => api.get(`/leads/${leadId}/proposals`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { loadOfferings(); loadList(); api.get("/proposals/setup").then((r) => setSetup(r.data)).catch(() => {}); api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {}); }, [leadId]);
  useEffect(() => {
    api.get("/tenant/currency").then((r) => { setCurCfg(r.data); setForm((f) => ({ ...f, currency: f.currency || r.data.region_currency_defaults?.[region] || r.data.reporting_currency })); }).catch(() => {});
  }, [region]);

  const openProposal = async (pid) => {
    const r = await api.get(`/proposals/${pid}`);
    setSel(r.data); setVerIdx((r.data.versions || []).length - 1);
  };

  const generate = async () => {
    if (!form.offering_id) { toast.error("Pick an offering first"); return; }
    setBusy(true);
    try {
      const payload = {
        offering_id: form.offering_id, title: form.title,
        list_amount: form.list_amount ? Number(form.list_amount) : null,
        discount_amount: form.discount_amount ? Number(form.discount_amount) : null,
        instructions: form.instructions, currency: form.currency || undefined,
        partners: partners.filter((p) => p.vendor_id).map((p) => ({ vendor_id: p.vendor_id, role: p.role || null, amount: p.amount === "" ? null : Number(p.amount) })),
      };
      const r = await runAiJob(`/leads/${leadId}/proposals`, payload, setElapsed);
      toast.success("Proposal drafted");
      await loadList();
      await openProposal(r.proposal.id);
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Generation failed"); }
    finally { setBusy(false); setElapsed(0); }
  };

  const doRevise = async () => {
    setBusy(true);
    try {
      await runAiJob(`/proposals/${sel.proposal.id}/revise`, {
        instructions: revise.instructions, change_note: revise.change_note,
        list_amount: revise.list_amount ? Number(revise.list_amount) : null,
        discount_amount: revise.discount_amount ? Number(revise.discount_amount) : null,
      });
      toast.success("New version drafted");
      setRevise({ open: false, instructions: "", change_note: "", list_amount: "", discount_amount: "" });
      await loadList(); await openProposal(sel.proposal.id); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Revise failed"); }
    finally { setBusy(false); setElapsed(0); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/proposals/${sel.proposal.id}/email/draft`);
      setCompose({ open: true, to: r.data.to || "", subject: r.data.subject || "", body: r.data.body || "", mode: r.data.proposal_mode, sending: false });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not prepare email"); }
    finally { setBusy(false); }
  };

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectCat, setRejectCat] = useState("Price / budget");
  const [rejectNote, setRejectNote] = useState("");
  const outcome = async (status, force = false, extra = {}) => {
    if (status === "rejected" && !extra.reason_category) { setRejectOpen(true); return; }
    if (status === "accepted" && !force && !window.confirm(`Mark accepted? The account's deal value becomes the proposal net (${sel.proposal.currency} ${Number(sel.proposal.net_amount ?? sel.proposal.list_amount ?? 0).toLocaleString()}) and the deal is marked WON.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/proposals/${sel.proposal.id}/outcome`, { status, force, ...extra });
      toast.success(status === "accepted" ? `Deal won · value ${Number(r.data.deal_value || 0).toLocaleString()}` : `Marked rejected · ${r.data.reason_category}`);
      setRejectOpen(false); setRejectNote("");
      loadList(); openProposal(sel.proposal.id); onChange?.();
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(`${e.response.data.detail}\n\nOverwrite the manual value?`)) return outcome(status, true, extra);
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    setCompose((c) => ({ ...c, sending: true }));
    try {
      const r = await api.post(`/proposals/${sel.proposal.id}/email/send`, { to: compose.to, subject: compose.subject, body: compose.body });
      if (r.data?.pending_approval) toast.info(r.data.message || "Submitted for approval");
      else toast.success(`Proposal emailed to ${r.data.sent_to || compose.to}`);
      setCompose({ open: false, to: "", subject: "", body: "", sending: false });
      await loadList(); await openProposal(sel.proposal.id); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); setCompose((c) => ({ ...c, sending: false })); }
  };

  const download = async (kind) => {
    try {
      const v = sel.versions[verIdx]?.version;
      const r = await api.get(`/proposals/${sel.proposal.id}/export.${kind}?version=${v}&envelope=${envelope}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `proposal_v${v}${envelope === "combined" ? "" : "_" + envelope}.${kind}`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Export failed"); }
  };

  const downloadPack = async () => {
    try {
      const v = sel.versions[verIdx]?.version;
      const r = await api.get(`/proposals/${sel.proposal.id}/submission-pack.zip?version=${v}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `submission_pack_v${v}.zip`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Pack export failed"); }
  };

  const del = async () => {
    if (!window.confirm("Remove this proposal from the deal?")) return;
    try { await api.delete(`/proposals/${sel.proposal.id}`); toast.success("Proposal removed"); setSel(null); await loadList(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ver = sel?.versions?.[verIdx];
  const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]";

  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="proposal-panel">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-4 h-4 text-[#1C2530]" strokeWidth={1.5} />
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Proposal Intelligence</div>
      </div>

      {/* Generate */}
      <div className="border border-[#DFDAD0] bg-[#F5F3EE] p-4 mb-5">
        {setup && !setup.ready ? (
          <div data-testid="proposal-setup-incomplete" className="text-sm text-[#8a4a12]">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#E2711D] mb-1">Proposal setup incomplete</div>
            Missing {!setup.has_active_offering ? "an active offering" : ""}{(!setup.has_active_offering && !setup.threshold_set) ? " and " : ""}{!setup.threshold_set ? "a discount threshold" : ""}.{" "}
            {setup.owner?.name ? <>Contact <strong>{setup.owner.name}</strong> to complete Proposal Settings.</> : "Ask your workspace admin to complete Proposal Settings."}
          </div>
        ) : offerings.length === 0 ? (
          <div className="font-mono text-xs text-[#1C2530]/50">NO_OFFERINGS · an Admin or Sales Lead must create an offering in Offerings first.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Offering</label>
              <select value={form.offering_id} onChange={(e) => setForm({ ...form, offering_id: e.target.value })} data-testid="proposal-offering-select" className={`${inp} w-full mt-1`}>
                <option value="">Select an offering…</option>
                {offerings.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Title (optional)</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="proposal-title" placeholder="Auto from account" className={`${inp} w-full mt-1`} />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">List amount · currency</label>
              <div className="flex gap-2 mt-1">
                <input type="number" value={form.list_amount} onChange={(e) => setForm({ ...form, list_amount: e.target.value })} data-testid="proposal-list-amount" placeholder="e.g. 50000" className={`${inp} w-full`} />
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="proposal-currency-select" className={inp}>
                  {(curCfg?.all_currencies || [form.currency || "USD"]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {curCfg && form.currency && form.currency !== curCfg.reporting_currency && (
                <div className="font-mono text-[9px] text-[#1C2530]/50 mt-1" data-testid="proposal-currency-hint">Snapshotted to {curCfg.reporting_currency} at the FX rate on file (generation fails if none exists).</div>
              )}
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Discounted amount off (optional)</label>
              <input type="number" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} data-testid="proposal-discount-amount" placeholder="e.g. 5000" className={`${inp} w-full mt-1`} />
            </div>
            <div className="md:col-span-2" data-testid="proposal-partners">
              <div className="flex items-center justify-between">
                <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Delivery partners from Vendor Directory (optional) · appear as rows in Commercial Terms</label>
                <button type="button" onClick={() => setPartners([...partners, { vendor_id: "", role: "", amount: "" }])} disabled={vendors.length === 0} data-testid="proposal-add-partner"
                  className="font-mono text-[9px] uppercase text-[#3E8F99] hover:underline disabled:opacity-40">+ Add partner</button>
              </div>
              {vendors.length === 0 && <div className="text-[11px] text-[#1C2530]/45 mt-1">No vendors in the directory yet.</div>}
              {partners.map((p, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2 mt-2" data-testid={`proposal-partner-row-${i}`}>
                  <select value={p.vendor_id} onChange={(e) => setPartners(partners.map((x, j) => j === i ? { ...x, vendor_id: e.target.value } : x))} data-testid={`proposal-partner-vendor-${i}`} className={inp}>
                    <option value="">Pick vendor…</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.category}{v.domain ? ` / ${v.domain}` : ""}</option>)}
                  </select>
                  <input value={p.role} onChange={(e) => setPartners(partners.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} data-testid={`proposal-partner-role-${i}`} placeholder="Role, e.g. delivers the VR safety module" className={inp} />
                  <input type="number" value={p.amount} onChange={(e) => setPartners(partners.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} data-testid={`proposal-partner-amount-${i}`} placeholder="Amount (blank = included)" className={inp} />
                  <button type="button" onClick={() => setPartners(partners.filter((_, j) => j !== i))} data-testid={`proposal-partner-remove-${i}`} className="font-mono text-[9px] uppercase text-[#B23A2E] px-2">Remove</button>
                </div>
              ))}
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Extra instructions (optional)</label>
              <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="proposal-instructions" placeholder="Emphasize compliance, mention pilot phase…" className={`${inp} w-full mt-1 h-16 resize-none`} />
            </div>
            <div className="md:col-span-2">
              <button onClick={generate} disabled={busy} data-testid="proposal-generate-btn"
                className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">
                <Sparkles className="w-3.5 h-3.5" /> {busy ? `Drafting… ${elapsed}s` : "Draft with Proposal AI"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Existing proposals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="space-y-2">
          <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50 mb-1">Proposals ({list.length})</div>
          {list.length === 0 && <div className="font-mono text-xs text-[#1C2530]/40">NO_PROPOSALS</div>}
          {list.map((p) => (
            <button key={p.id} onClick={() => openProposal(p.id)} data-testid={`proposal-row-${p.id}`}
              className={`w-full text-left border p-2 transition-colors ${sel?.proposal?.id === p.id ? "border-[#1C2530] bg-[#EFECE5]" : "border-[#DFDAD0] hover:bg-[#F5F3EE]"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-[#1C2530] truncate">{p.title}</span>
                <ChevronRight className="w-3.5 h-3.5 text-[#1C2530]/40 shrink-0" />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Pill status={p.status} />
                <span className="font-mono text-[9px] text-[#1C2530]/50">v{p.current_version} · {p.discount_pct || 0}% off{p.margin_pct != null ? ` · margin ${p.margin_pct}%` : ""}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {!sel ? (
            <div className="font-mono text-xs text-[#1C2530]/40 border border-dashed border-[#DFDAD0] p-8 text-center">SELECT_A_PROPOSAL</div>
          ) : (
            <div className="border border-[#DFDAD0]">
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-[#DFDAD0] bg-[#F5F3EE]">
                <div className="flex items-center gap-2">
                  <Pill status={sel.proposal.status} />
                  <select value={verIdx} onChange={(e) => setVerIdx(Number(e.target.value))} data-testid="proposal-version-select" className="border border-[#DFDAD0] px-2 py-1 text-xs font-mono bg-white outline-none">
                    {sel.versions.map((v, i) => <option key={v.id} value={i}>v{v.version} · {v.change_note}</option>)}
                  </select>
                  <MarginBadge p={sel.proposal} />
                </div>
                <div className="flex items-center gap-2">
                  {setup?.proposal_mode === "split" && (
                    <select value={envelope} onChange={(e) => setEnvelope(e.target.value)} data-testid="proposal-envelope-select" className="border border-[#DFDAD0] px-2 py-1 text-[10px] font-mono bg-white outline-none">
                      <option value="combined">Combined</option>
                      <option value="technical">Technical</option>
                      <option value="commercial">Commercial</option>
                    </select>
                  )}
                  <button onClick={() => download("pdf")} data-testid="proposal-export-pdf" className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Download className="w-3 h-3" /> PDF</button>
                  <button onClick={() => download("docx")} data-testid="proposal-export-docx" className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Download className="w-3 h-3" /> DOCX</button>
                  {setup?.proposal_mode === "split" && (
                    <button onClick={downloadPack} data-testid="proposal-submission-pack" className="flex items-center gap-1 border border-[#3E8F99]/50 text-[#276169] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#3E8F99]/10"><Download className="w-3 h-3" /> Pack</button>
                  )}
                  <button onClick={() => setRevise((r) => ({ ...r, open: !r.open }))} data-testid="proposal-revise-btn" className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><RefreshCw className="w-3 h-3" /> Revise</button>
                  <button onClick={del} data-testid="proposal-delete-btn" className="flex items-center gap-1 border border-[#B23A2E]/40 text-[#B23A2E] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#B23A2E]/10"><Trash2 className="w-3 h-3" /> Delete</button>
                  <button onClick={send} disabled={busy || sel.proposal.status === "sent"} data-testid="proposal-send-btn" className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#2a3644] disabled:opacity-40"><Send className="w-3 h-3" /> {sel.proposal.status === "sent" ? "Sent" : "Email client"}</button>
                  {["sent", "revised", "draft"].includes(sel.proposal.status) && (<>
                    <button onClick={() => outcome("accepted")} disabled={busy} data-testid="proposal-accept-btn" className="flex items-center gap-1 border border-[#2F7A3E] text-[#2F7A3E] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#2F7A3E]/10 disabled:opacity-40">Client accepted</button>
                    <button onClick={() => outcome("rejected")} disabled={busy} data-testid="proposal-reject-btn" className="flex items-center gap-1 border border-[#B23A2E]/50 text-[#B23A2E] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#B23A2E]/10 disabled:opacity-40">Rejected</button>
                  </>)}
                  {["accepted", "rejected"].includes(sel.proposal.status) && <span className="font-mono text-[10px] text-[#1C2530]/50" data-testid="proposal-outcome-note">{sel.proposal.status} by {sel.proposal.decided_by}{sel.proposal.reason_category ? ` · ${sel.proposal.reason_category}` : ""}{sel.proposal.outcome_reason ? ` · ${sel.proposal.outcome_reason}` : ""}</span>}
                  {rejectOpen && (
                    <div className="w-full mt-2 border border-[#B23A2E]/40 bg-[#B23A2E]/5 p-3 flex flex-wrap items-center gap-2" data-testid="reject-picker">
                      <span className="font-mono text-[9px] uppercase text-[#B23A2E]">Why did the client reject?</span>
                      <select value={rejectCat} onChange={(e) => setRejectCat(e.target.value)} data-testid="reject-category" className="border border-[#DFDAD0] bg-white px-2 py-1 text-xs">
                        {LOSS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Note (optional)" data-testid="reject-note" className="border border-[#DFDAD0] bg-white px-2 py-1 text-xs flex-1 min-w-[160px]" />
                      <button onClick={() => outcome("rejected", false, { reason_category: rejectCat, reason: rejectNote || null })} disabled={busy} data-testid="reject-confirm" className="bg-[#B23A2E] text-white px-3 py-1 font-mono text-[10px] uppercase disabled:opacity-40">Confirm</button>
                      <button onClick={() => setRejectOpen(false)} className="font-mono text-[10px] uppercase text-[#1C2530]/50 px-2">Cancel</button>
                    </div>
                  )}
                </div>
              </div>

              {revise.open && (
                <div className="p-3 border-b border-[#DFDAD0] bg-[#EFECE5] space-y-2" data-testid="proposal-revise-box">
                  <textarea value={revise.instructions} onChange={(e) => setRevise({ ...revise, instructions: e.target.value })} data-testid="proposal-revise-instructions" placeholder="What changed in negotiation? (e.g. add 10% discount, extend timeline)" className={`${inp} w-full h-14 resize-none`} />
                  <div className="flex flex-wrap gap-2">
                    <input value={revise.change_note} onChange={(e) => setRevise({ ...revise, change_note: e.target.value })} data-testid="proposal-revise-note" placeholder="Version note" className={`${inp} flex-1 min-w-[140px]`} />
                    <input type="number" value={revise.list_amount} onChange={(e) => setRevise({ ...revise, list_amount: e.target.value })} placeholder="New list amt" className={`${inp} w-28`} />
                    <input type="number" value={revise.discount_amount} onChange={(e) => setRevise({ ...revise, discount_amount: e.target.value })} placeholder="New disc amt" className={`${inp} w-28`} />
                    <button onClick={doRevise} disabled={busy} data-testid="proposal-revise-submit" className="bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase hover:bg-[#2a3644] disabled:opacity-40">Generate v{sel.proposal.current_version + 1}</button>
                  </div>
                </div>
              )}

              <div className="p-4 max-h-[520px] overflow-y-auto space-y-1" data-testid="proposal-preview">
                {ver ? renderMd(ver.markdown) : <div className="font-mono text-xs text-[#1C2530]/40">NO_CONTENT</div>}
              </div>
              {compose.open && (
                <div className="border-t border-[#DFDAD0] p-4 bg-[#EFECE5] space-y-2" data-testid="proposal-email-compose">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Email to client · final review before sending</div>
                  {compose.mode === "split" && <div className="text-[11px] text-[#276169]">Split mode — only the <strong>Commercial</strong> proposal PDF will be attached.</div>}
                  <input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} data-testid="proposal-email-to" placeholder="recipient@company.com" className={`${inp} w-full`} />
                  <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="proposal-email-subject" className={`${inp} w-full`} />
                  <textarea value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="proposal-email-body" className={`${inp} w-full h-28 resize-none`} />
                  <div className="flex items-center gap-2">
                    <button onClick={sendEmail} disabled={compose.sending} data-testid="proposal-email-send-btn" className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase hover:bg-[#2a3644] disabled:opacity-40"><Send className="w-3 h-3" /> {compose.sending ? "Sending…" : "Send to client"}</button>
                    <button onClick={() => setCompose({ ...compose, open: false })} data-testid="proposal-email-cancel" className="border border-[#DFDAD0] px-3 py-1.5 text-xs font-mono uppercase hover:bg-white">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
